/**
 * Mnemonic Display Component
 *
 * Shows the recovery phrase for backup
 */

import { useState } from "react";
import { Copy, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useBrowserWallet } from "@nullaone/wallet-react";

interface MnemonicDisplayProps {
  mnemonic?: string;
}

export function MnemonicDisplay({
  mnemonic: propMnemonic,
}: MnemonicDisplayProps) {
  const { closeModal, markBackedUp } = useBrowserWallet();
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // This component can receive mnemonic as prop (during creation)
  // or could be used standalone for backup display
  const mnemonic = propMnemonic;

  if (!mnemonic) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">No mnemonic available</p>
      </div>
    );
  }

  const words = mnemonic.split(" ");

  const handleCopy = async () => {
    await navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDone = async () => {
    if (confirmed) {
      await markBackedUp();
      closeModal();
    }
  };

  return (
    <div className="space-y-6 py-4">
      {/* Warning */}
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-600">
              Write Down These Words
            </p>
            <p className="text-xs text-amber-600/80">
              This is your wallet recovery phrase. Write it down on paper and
              store it in a secure location. Never share it with anyone.
            </p>
          </div>
        </div>
      </div>

      {/* Mnemonic grid */}
      <div className="grid grid-cols-3 gap-2">
        {words.map((word, index) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm"
          >
            <span className="text-xs text-muted-foreground w-5 text-right">
              {index + 1}.
            </span>
            <span className="font-mono">{word}</span>
          </div>
        ))}
      </div>

      {/* Copy button */}
      <Button variant="outline" className="w-full gap-2" onClick={handleCopy}>
        {copied ? (
          <>
            <Check className="h-4 w-4" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="h-4 w-4" />
            Copy to Clipboard
          </>
        )}
      </Button>

      {/* Confirmation checkbox */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="confirm-saved"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
        <Label htmlFor="confirm-saved" className="text-sm cursor-pointer">
          I have written down my recovery phrase and stored it safely
        </Label>
      </div>

      {/* Done button */}
      <Button
        className="w-full"
        size="lg"
        disabled={!confirmed}
        onClick={handleDone}
      >
        Done
      </Button>
    </div>
  );
}
